import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider } from '@/shared/ui/atoms';
import { formatGrade, GRADE_STEP, parseGradeInput, sanitizeGradeInput } from '../../../../utils';

export interface CompletenessFieldProps {
  /** Completude atual, de 0 a 100 — pode ser fracionária */
  readonly value: number;
  /** Altera a completude (slider ou input numérico) */
  readonly onChange: (next: number) => void;
}

/**
 * Campo de completude da correção — slider e input numérico sincronizados.
 *
 * O número é um campo de texto com máscara própria, não um `input[type=number]`: o
 * nativo aceitava `008`, `000` e notação científica, e engolia a vírgula decimal.
 */
export function CompletenessField({ value, onChange }: CompletenessFieldProps) {
  const { t } = useTranslation('examCorrection');
  // Texto em edição. Enquanto existe, ele manda na exibição — é o que permite
  // digitar '85,' antes da casa decimal. Fora da digitação a exibição volta a
  // espelhar a prop, que a sugestão da IA anima até a nota sugerida.
  const [draft, setDraft] = useState<string | null>(null);

  function handleTextChange(event: ChangeEvent<HTMLInputElement>) {
    const sanitized = sanitizeGradeInput(event.target.value);
    // O caractere recusado precisa sair da tela ainda neste evento: quando o texto
    // sanitizado é igual ao já renderizado ('00' → '0'), o React não reescreve o DOM
    // e o zero extra ficaria visível.
    event.target.value = sanitized;
    setDraft(sanitized);
    onChange(parseGradeInput(sanitized));
  }

  return (
    <div className="mt-xs flex flex-col gap-nano">
      <div className="flex items-center gap-quark">
        <label htmlFor="correction-completeness" className="text-ctx-content-title ts-subtitle-xs">
          {t('modal.fields.completeness')}
        </label>
        <span className="text-ctx-status-negative ts-subtitle-xs" aria-hidden="true">
          *
        </span>
      </div>
      <div className="flex items-center gap-sm">
        {/* O passo acompanha a precisão das notas: um passo inteiro faria o
            input[type=range] encaixar uma nota fracionária (0,9) no múltiplo mais
            próximo, divergindo do número exibido ao lado. */}
        <Slider
          id="correction-completeness"
          min={0}
          max={100}
          step={GRADE_STEP}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          aria-label={t('modal.fields.completeness')}
          aria-valuetext={`${formatGrade(value)}%`}
          className="flex-1"
        />
        <div className="flex items-center gap-quark rounded-sm border-thin border-ctx-layout-border bg-ctx-layout-surface px-xs py-quark">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft ?? formatGrade(value)}
            onChange={handleTextChange}
            onBlur={() => setDraft(null)}
            className="bg-base-neutral-none w-[3.6rem] text-center text-ctx-content-title outline-none tabular-nums ts-paragraph-xs"
            aria-label={t('modal.fields.completeness')}
          />
          <span className="text-ctx-content-placeholder ts-caption-xxs">%</span>
        </div>
      </div>
    </div>
  );
}
